import type { MutableRefObject } from "react";
import type {
  Message,
  ExportUrdfResultMessage,
  ExportUrdfProgressMessage,
  SaveConfigResultMessage,
  SaveConnectableLinksResultMessage,
  RenameJointResultMessage,
  JointGroupResultMessage,
  JointSettingsResultMessage,
  UrdfForConfigResultMessage,
  UploadModuleResultMessage,
} from "./protocol.generated";

type MessageByType<T extends Message["type"]> = Extract<Message, { type: T }>;

type InboundMessageHandler<T extends Message["type"]> = (
  message: MessageByType<T>,
  context: MessageHandlerContext,
) => void;

type InboundMessageHandlerTable = {
  [K in Message["type"]]?: InboundMessageHandler<K>;
};

interface MessageHandlerState {
  addGuiFolder: (message: MessageByType<"add_gui_folder">) => void;
  addGuiButton: (message: MessageByType<"add_gui_button">) => void;
  addGuiSlider: (message: MessageByType<"add_gui_slider">) => void;
  addGuiDropdown: (message: MessageByType<"add_gui_dropdown">) => void;
  addGuiCheckbox: (message: MessageByType<"add_gui_checkbox">) => void;
  addGuiText: (message: MessageByType<"add_gui_text">) => void;
  removeGui: (uuid: MessageByType<"remove_gui">["uuid"]) => void;
  updateGui: (
    uuid: MessageByType<"update_gui">["uuid"],
    updates: MessageByType<"update_gui">["updates"],
  ) => void;
  addMesh: (message: MessageByType<"add_mesh">) => void;
  addMeshGlb: (message: MessageByType<"add_mesh_glb">) => void;
  removeMesh: (uuid: MessageByType<"remove_mesh">["uuid"]) => void;
  updateTransforms: (updates: MessageByType<"update_transforms">["updates"]) => void;
  setCamera: (
    position: MessageByType<"set_camera">["position"],
    target: MessageByType<"set_camera">["target"],
  ) => void;
  setBackgroundColor: (color: MessageByType<"set_background">["color"]) => void;
  setModuleList: (message: MessageByType<"set_module_list">) => void;
  setSelectedModule: (message: MessageByType<"set_selected_module">) => void;
  setConnectionPreview: (message: MessageByType<"set_connection_preview">) => void;
  setInstanceList: (message: MessageByType<"set_instance_list">) => void;
  addIKGizmos: (message: MessageByType<"add_ik_gizmo">) => void;
  removeIKGizmos: () => void;
  updateIKResult: (message: MessageByType<"update_ik_result">) => void;
  setJointList: (message: MessageByType<"set_joint_list">) => void;
  updateJointValues: (jointValues: MessageByType<"update_joint_values">["joint_values"]) => void;
  setModuleLinks: (message: MessageByType<"set_module_links">) => void;
  setEndCoordsList: (message: MessageByType<"set_end_coords_list">) => void;
  setAvailableLinks: (message: MessageByType<"set_available_links">) => void;
  setHighlight: (message: MessageByType<"set_highlight">) => void;
  setSelfCollisionState: (message: MessageByType<"set_self_collision_state">) => void;
  setEndCoordsGizmo: (message: MessageByType<"set_end_coords_gizmo">) => void;
  setEndCoordsLocal: (message: MessageByType<"end_coords_local_update">) => void;
  setJointGroups: (message: MessageByType<"set_joint_groups">) => void;
  setSuggestedGroups: (message: MessageByType<"set_suggested_groups">) => void;
  setMimicList: (settings: MessageByType<"set_mimic_list">["mimic_settings"]) => void;
  setJointAxisVisualization: (message: MessageByType<"set_joint_axis_visualization">) => void;
  setChainJointsResult: (message: MessageByType<"set_chain_joints">) => void;
}

export interface MessageHandlerContext {
  state: MessageHandlerState;
  handleExportResult: (message: ExportUrdfResultMessage) => void;
  handleExportProgress: (message: ExportUrdfProgressMessage) => void;
  handleSaveConfigResult: (message: SaveConfigResultMessage) => void;
  handleSaveConnectableLinksResult: (message: SaveConnectableLinksResultMessage) => void;
  handleRenameJointResult: (message: RenameJointResultMessage) => void;
  handleJointGroupResult: (message: JointGroupResultMessage) => void;
  handleJointSettingsResult: (message: JointSettingsResultMessage) => void;
  urdfForConfigCallbackRef: MutableRefObject<((result: UrdfForConfigResultMessage) => void) | null>;
  uploadModuleCallbackRef: MutableRefObject<((result: UploadModuleResultMessage) => void) | null>;
}

export const inboundMessageHandlers: InboundMessageHandlerTable = {
  add_gui_folder: (message, { state }) => state.addGuiFolder(message),
  add_gui_button: (message, { state }) => state.addGuiButton(message),
  add_gui_slider: (message, { state }) => state.addGuiSlider(message),
  add_gui_dropdown: (message, { state }) => state.addGuiDropdown(message),
  add_gui_checkbox: (message, { state }) => state.addGuiCheckbox(message),
  add_gui_text: (message, { state }) => state.addGuiText(message),
  remove_gui: (message, { state }) => state.removeGui(message.uuid),
  update_gui: (message, { state }) => state.updateGui(message.uuid, message.updates),
  add_mesh: (message, { state }) => state.addMesh(message),
  add_mesh_glb: (message, { state }) => state.addMeshGlb(message),
  remove_mesh: (message, { state }) => state.removeMesh(message.uuid),
  update_transforms: (message, { state }) => state.updateTransforms(message.updates),
  set_camera: (message, { state }) => state.setCamera(message.position, message.target),
  set_background: (message, { state }) => state.setBackgroundColor(message.color),
  set_module_list: (message, { state }) => state.setModuleList(message),
  set_selected_module: (message, { state }) => state.setSelectedModule(message),
  set_connection_preview: (message, { state }) => state.setConnectionPreview(message),
  set_instance_list: (message, { state }) => state.setInstanceList(message),
  add_ik_gizmo: (message, { state }) => state.addIKGizmos(message),
  remove_ik_gizmo: (_message, { state }) => state.removeIKGizmos(),
  update_ik_result: (message, { state }) => state.updateIKResult(message),
  set_joint_list: (message, { state }) => state.setJointList(message),
  update_joint_values: (message, { state }) => state.updateJointValues(message.joint_values),
  export_urdf_result: (message, { handleExportResult }) => handleExportResult(message),
  export_urdf_progress: (message, { handleExportProgress }) => handleExportProgress(message),
  save_config_result: (message, { handleSaveConfigResult }) => handleSaveConfigResult(message),
  set_module_links: (message, { state }) => state.setModuleLinks(message),
  save_connectable_links_result: (message, { handleSaveConnectableLinksResult }) =>
    handleSaveConnectableLinksResult(message),
  set_end_coords_list: (message, { state }) => state.setEndCoordsList(message),
  set_available_links: (message, { state }) => state.setAvailableLinks(message),
  set_highlight: (message, { state }) => state.setHighlight(message),
  set_self_collision_state: (message, { state }) => state.setSelfCollisionState(message),
  set_end_coords_gizmo: (message, { state }) => state.setEndCoordsGizmo(message),
  end_coords_local_update: (message, { state }) => state.setEndCoordsLocal(message),
  rename_joint_result: (message, { handleRenameJointResult }) => handleRenameJointResult(message),
  set_joint_groups: (message, { state }) => state.setJointGroups(message),
  set_suggested_groups: (message, { state }) => state.setSuggestedGroups(message),
  joint_group_result: (message, { handleJointGroupResult }) => handleJointGroupResult(message),
  joint_settings_result: (message, { handleJointSettingsResult }) => handleJointSettingsResult(message),
  set_mimic_list: (message, { state }) => state.setMimicList(message.mimic_settings),
  set_joint_axis_visualization: (message, { state }) => state.setJointAxisVisualization(message),
  set_chain_joints: (message, { state }) => state.setChainJointsResult(message),
  urdf_for_config_result: (message, { urdfForConfigCallbackRef }) => {
    if (urdfForConfigCallbackRef.current) {
      urdfForConfigCallbackRef.current(message);
    }
  },
  upload_module_result: (message, { uploadModuleCallbackRef }) => {
    if (uploadModuleCallbackRef.current) {
      uploadModuleCallbackRef.current(message);
    }
  },
};

const getInboundMessageHandler = <T extends Message>(
  message: T,
): InboundMessageHandler<T["type"]> | undefined => {
  return inboundMessageHandlers[message.type] as InboundMessageHandler<T["type"]> | undefined;
};

export function dispatchInboundMessage(message: Message, context: MessageHandlerContext): boolean {
  const handler = getInboundMessageHandler(message);
  if (!handler) {
    return false;
  }
  handler(message, context);
  return true;
}
